/**
 * Adapts the v2 availability/schedule API response shapes to the shapes the
 * frontend (types, components) expects. This is a permanent internal
 * shape-adapter, not a v1 compatibility shim — it has multiple consumers
 * across `react-query-hooks/availability.ts`, `react-query-hooks/schedule`,
 * and `components/offering-form`.
 */

import type {TAvailabilityApiResponse} from "../availability";
import type {
  CreateUserScheduleApiResponse,
  CreateUserSchedulesApiResponse,
  FetchSessionsNotUsingScheduleApiResponse,
} from "../schedule/types";

type DaySlots = {from: string; to: string; id?: number}[];

/** v2 availability response - extract data and ensure shape */
export function normalizeV2AvailabilityResponse(data: unknown): TAvailabilityApiResponse {
  const d = data as Record<string, DaySlots>;
  const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
  const result: TAvailabilityApiResponse = {
    sun: [],
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
  };
  for (const day of days) {
    result[day] = Array.isArray(d[day])
      ? d[day].map((slot) => ({from: slot.from, to: slot.to}))
      : [];
  }
  return result;
}

/** v2 fetch schedules response - extract schedules array */
export function normalizeV2SchedulesResponse(
  data: unknown
): CreateUserSchedulesApiResponse {
  const d = data as {schedules?: CreateUserScheduleApiResponse[]; total?: number};
  return {
    schedules: Array.isArray(d?.schedules)
      ? d.schedules.filter((schedule) => schedule != null)
      : [],
  };
}

/** v2 create schedule response - extract schedule from data.schedule */
export function normalizeV2CreateScheduleResponse(raw: unknown): {
  data: CreateUserScheduleApiResponse;
} {
  const d = raw as {schedule?: CreateUserScheduleApiResponse};
  const schedule = d?.schedule;
  return {
    data: schedule ?? ({} as CreateUserScheduleApiResponse),
  };
}

/** Normalize sessions list responses and drop null/invalid entries from API arrays. */
export function normalizeSessionsListResponse(
  data: unknown
): FetchSessionsNotUsingScheduleApiResponse {
  const d = data as {sessions?: unknown[] | null; data?: unknown[]};
  const rawSessions = Array.isArray(d?.sessions)
    ? d.sessions
    : Array.isArray(d?.data)
    ? d.data
    : Array.isArray(data)
    ? data
    : [];

  const sessions = rawSessions
    .map((item) => {
      if (item == null || typeof item !== "object" || Array.isArray(item)) {
        return null;
      }
      const record = item as Record<string, unknown>;
      const nestedSession =
        record.session != null &&
        typeof record.session === "object" &&
        !Array.isArray(record.session)
          ? (record.session as Record<string, unknown>)
          : null;

      const id = record.id ?? nestedSession?.id;
      const name = record.name ?? record.serviceName ?? nestedSession?.name;

      if (id == null && !name) {
        return null;
      }

      return {
        ...record,
        ...(nestedSession ?? {}),
        id: id as number,
        name: String(name ?? "Untitled session"),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item != null);

  return {sessions} as FetchSessionsNotUsingScheduleApiResponse;
}
